import { useSortable } from '@dnd-kit/sortable';
import {
  createColumnHelper,
  type AccessorFnColumnDef,
  type RowData,
} from '@tanstack/react-table';
import { clsx } from 'clsx';
import { type JSX } from 'react';
import { useIntl } from 'react-intl';

import { DragAllDirectionIcon } from '@dynatrace/strato-icons';

import * as styles from './RowOrder.sty.js';
import { Button } from '../../../../buttons/button/Button.js';
import { useModality } from '../../../../core/hooks/useModality.js';
import { focusRingStatic } from '../../../../core/styles/focusRingStatic.sty.js';
import { Tooltip } from '../../../../overlays/tooltip/Tooltip.js';
import { BuiltinColumnHeaderName } from '../../components/BuiltinColumnHeaderName.js';
import { DataTableDefaultCell } from '../../components/DataTableDefaultCell.js';
import {
  DATA_TABLE_BUILTIN_COLUMN_MIN_WIDTH,
  DATA_TABLE_ROW_DRAG_COLUMN_ID,
} from '../../constants.js';
import { dataTableMessages } from '../../messages.js';
import type { TDataTableFeatures } from '../types/features.types.js';

const rowOrderColumnHelper = createColumnHelper<TDataTableFeatures, RowData>();

/**
 * Derives the sort direction used for the drag handle's "clear sort order"
 * tooltip from the current sorting state: 'ascending', 'descending', 'mixed',
 * or undefined when the table is not sorted.
 */
const getSortDirection = (
  sorting: { desc: boolean }[] | undefined,
): string | undefined => {
  if (sorting === undefined || sorting.length === 0) {
    return undefined;
  }
  if (sorting.every((sort) => sort.desc)) {
    return 'descending';
  }
  if (sorting.every((sort) => !sort.desc)) {
    return 'ascending';
  }
  return 'mixed'; // multi-sort with some asc and some desc sorted columns
};

const RowDragHandleCell = ({
  rowId,
  disabled,
  isDraggingActive,
  sortDirection,
  disabledTooltip,
}: {
  /**
   * The id of the row this handle belongs to.
   */
  rowId: string;
  /**
   * Whether drag and drop is disabled for this row.
   */
  disabled: boolean;
  /**
   * Whether this row is the one currently being dragged.
   */
  isDraggingActive: boolean;
  /**
   * The sort direction for the sorted tooltip: 'ascending', 'descending', 'mixed',
   * or undefined if not sorted.
   */
  sortDirection: string | undefined;
  /**
   * The consumer-provided tooltip shown when the handle is disabled via
   * `disableRowDragAndDrop`. Superseded by the built-in default while sorted.
   */
  disabledTooltip: string | undefined;
}): JSX.Element => {
  const intl = useIntl();
  const modality = useModality();
  const { attributes, listeners } = useSortable({
    id: rowId,
  });

  const isKeyboardDragging = isDraggingActive && modality === 'keyboard';
  const rowDragHandleLabel = intl.formatMessage(
    dataTableMessages.rowDragAndDropLabel,
    { rowId },
  );

  // Sorted tooltip takes precedence over consumer-provided disabled tooltip.
  const effectiveTooltip =
    sortDirection !== undefined
      ? intl.formatMessage(dataTableMessages.rowDragAndDropSortedTooltip, {
          sortDirection,
        })
      : disabledTooltip;
  const tooltipText = disabled ? effectiveTooltip : undefined;

  const dragHandle = (
    <Button
      size="condensed"
      variant="default"
      {...attributes}
      {...(disabled ? undefined : listeners)}
      aria-disabled={disabled}
      aria-label={rowDragHandleLabel}
      className={clsx(
        styles.dndHandle({
          disabled,
        }),
        // Apply the focus ring unconditionally (when: 'always')
        // to the dragged row's drag handle during keyboard dragging.
        isKeyboardDragging &&
          focusRingStatic({
            minimal: false,
            color: 'neutral',
            when: 'always',
          }),
      )}
      data-dt-children-name="RowOrderDragAndDrop"
    >
      <Button.Prefix>
        <DragAllDirectionIcon />
      </Button.Prefix>
    </Button>
  );

  return (
    <DataTableDefaultCell>
      {tooltipText ? (
        <Tooltip text={tooltipText}>{dragHandle}</Tooltip>
      ) : (
        dragHandle
      )}
    </DataTableDefaultCell>
  );
};

/**
 * Column definition for the row drag column.
 * @internal
 */
export const ROW_DRAG_COLUMN: AccessorFnColumnDef<
  TDataTableFeatures,
  RowData,
  string
> = rowOrderColumnHelper.accessor(() => '', {
  id: DATA_TABLE_ROW_DRAG_COLUMN_ID,
  alignment: 'center',
  header: () => (
    <BuiltinColumnHeaderName message={dataTableMessages.rowOrderColumnName} />
  ),
  width: 'content',
  minWidth: DATA_TABLE_BUILTIN_COLUMN_MIN_WIDTH,
  enableSorting: false,
  enableResizing: false,
  enableUserActions: false,
  enableHiding: false,
  meta: {
    builtin: true,
    hasCustomCellRenderer: true,
    hasCustomHeaderRenderer: true,
    builtinPlacement: 'prefix',
    cellDeps: (cell) => {
      const sorting = cell.row.table.atoms.sorting?.get();
      return [
        cell.row.getCanDrag(),
        getSortDirection(sorting) ?? '',
        cell.row.table.options.disabledRowDragAndDropTooltip?.(cell.row) ?? '',
      ];
    },
  },
  cell: ({ row, table }) => {
    const isDraggingActive = table.atoms.draggingRowInfo?.get()?.id === row.id;
    const sorting = table.atoms.sorting?.get();
    const disabledTooltip = table.options.disabledRowDragAndDropTooltip?.(row);
    const sortDirection = getSortDirection(sorting);

    return (
      <RowDragHandleCell
        rowId={row.id}
        disabled={!row.getCanDrag()}
        isDraggingActive={isDraggingActive}
        sortDirection={sortDirection}
        disabledTooltip={disabledTooltip}
      />
    );
  },
});
